// src/components/NewInvoice/InvoiceForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useGetAllCustomersQuery,
  useAddCustomerMutation,
} from "../../toolkitServices/customerApi";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { handleDropDowns } from "../../config/dropDownHelpers";
import {
  defaultDiamondFields,
  defaultStoneFields,
  InvoiceTopFields,
} from "../../config/fieldsHelper";
import Select from "react-select";
import { motion } from "framer-motion";
import {
  convertToDateInputFormat,
  formatDate,
  getTodayDate,
  threeDecimal,
} from "../../config/helperFunctions";
import { FaPlus, FaUndo, FaList, FaCheck, FaTrash } from "react-icons/fa";
import { FaCartShopping } from "react-icons/fa6";
// import InvoiceItemForm from "./InvoiceItemForm";

import {
  estimateGst as ORNAMENT_GST,
  estimateTds as tds,
} from "../../config/taxes";
import { estimateGst as gst } from "../../config/taxes";
// import InvoiceItemTypeTabs from "./InvoiceItemTypeTabs";
import QuickAddCustomerModal from "../other/QuickAddCustomerModal";
import CustomerAvatarHoverCard from "../other/CustomerAvatarHoverCard";
import {
  useAddInvoiceMutation,
  useUpdateInvoiceMutation,
  useGetLastInvoiceNumberQuery,
} from "../../toolkitServices/invoiceApi";
import toast from "react-hot-toast";
import {
  useAddEstimateMutation,
  useUpdateEstimateMutation,
  useGetLastEstimateNumberQuery,
  useDownloadEstimateReceiptPdfMutation,
} from "../../toolkitServices/estimateApi";
import { openEstimatePdfInNewTab } from "../../utils/receipts";

import { openInvoicePdfInNewTab } from "../../utils/receipts";
import ConfirmDialog from "../ConfirmDialog"; // ✅
import stockApi from "../../toolkitServices/stockApi";
import EstimateItemForm from "./EstimateItemForm";
import LooseStoneItemForm from "../NewInvoice/LooseStoneItemForm";
import LooseDiamondItemForm from "../NewInvoice/LooseDiamondItemForm";
import InvoiceItemForm from "../NewInvoice/InvoiceItemForm";
import InvoiceItemTypeTabs from "../NewInvoice/InvoiceItemTypeTabs";
import { useGetTenantEstimateTemplateQuery } from "../../toolkitServices/invoiceTemplateApi";
import { useDebounce } from "../../hooks/useDebounced";
import { useGetQuotationsQuery } from "../../toolkitServices/quotationApi";
import { useGetOtherSettingsQuery } from "../../toolkitServices/otherSettingsApi";
import InvoicePrintPreviewModal from "../InvoicePrintPreviewModal";

const CARD_HEIGHT_CLASS = "h-[32vh] sm:h-[32vh]";
const today = convertToDateInputFormat(getTodayDate());

const DEFAULTS = {
  customer: null,
  branch: null,
  invoiceNo: "",
  invoiceDate: convertToDateInputFormat(getTodayDate()),
  stockEntries: [],
  looseStones: [],
  looseDiamonds: [],
  totalDiscount: "",
  courierCharge: "",
  gstApply: false,
  gstRoundOff: false,
  payments: [],
  fineGold: "",
  fineSilver: "",
  advanceAmount: "",
  balanceAmount: "",
  grandTotalManual: "",
  tdsApply: undefined,
};

const baseSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "30px",
    height: "30px",
    borderColor: state.isFocused ? "#9481ce" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(148,129,206,.25)" : "none",
  }),
  menu: (b) => ({ ...b, zIndex: 40, fontSize: "12px" }),
  menuList: (base) => ({ ...base, maxHeight: "120px", overflowY: "auto" }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 6px",
    height: "30px",
    overflow: "hidden",
  }),
  indicatorsContainer: (base) => ({ ...base, height: "30px" }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  clearIndicator: (base) => ({ ...base, padding: "4px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
};

const customerSelectStyles = {
  ...baseSelectStyles,
  container: (base) => ({ ...base, width: "100%" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "calc(100% - 28px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  menuList: (base) => ({ ...base, maxHeight: "180px", overflowY: "auto" }),
};

const withErrorBorder = (stylesObj, hasError) => {
  if (!hasError) return stylesObj;
  return {
    ...stylesObj,
    control: (base, state) => {
      const baseCtrl = stylesObj.control
        ? stylesObj.control(base, state)
        : base;
      return {
        ...baseCtrl,
        borderColor: "#ef4444",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(239,68,68,0.20)" : "none",
        "&:hover": { borderColor: "#ef4444" },
      };
    },
  };
};

const inputCls = (hasError, extra = "", type = "") =>
  `w-full px-3 ${
    type === "date" ? "py-[3px]" : "py-1"
  } border rounded-md text-sm focus:outline-none focus:ring-2 ${
    hasError
      ? "border-red-400 focus:ring-red-300"
      : "border-gray-300 focus:ring-violet-400"
  } ${extra}`;

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const ADJUSTMENT_MODE = {
  DISCOUNT: "DISCOUNT",
  MAKING: "MAKING",
  MAKINGPERGRAM: "MAKINGPERGRAM",
  FIXEDAMOUNT: "FIXEDAMOUNT",
  FIXEDWASTAGE: "FIXEDWASTAGE",
};

const round2 = (v) => +Number(v || 0).toFixed(2);
const round3 = (v) => +Number(v || 0).toFixed(3);

const getRowAdjustmentSnapshot = (item) => {
  const makingPercentage = num(item?.makingPercentage);
  const makingPerGram = num(item?.makingPerGram);
  const fixedWastage = num(item?.fixedWastage);
  const fixedAmount = num(item?.fixedAmount);
  const netWt = num(item?.netWt);
  const ratePerGram = num(item?.ratePerGram);

  const makingPctAmount = round2(
    (netWt * ratePerGram * makingPercentage) / 100,
  );
  const makingPerGramAmount = round2(netWt * makingPerGram);
  const fixedWastageAmount = round2(ratePerGram * fixedWastage);
  const fixedAmountAmount = round2(fixedAmount);

  const activeModes = [];
  if (makingPercentage > 0) activeModes.push(ADJUSTMENT_MODE.MAKING);
  if (makingPerGram > 0) activeModes.push(ADJUSTMENT_MODE.MAKINGPERGRAM);
  if (fixedWastage > 0) activeModes.push(ADJUSTMENT_MODE.FIXEDWASTAGE);
  if (fixedAmount > 0) activeModes.push(ADJUSTMENT_MODE.FIXEDAMOUNT);

  return {
    makingPercentage,
    makingPerGram,
    fixedWastage,
    fixedAmount,
    netWt,
    ratePerGram,
    makingPctAmount,
    makingPerGramAmount,
    fixedWastageAmount,
    fixedAmountAmount,
    activeModes,
  };
};

const resolveRowAdjustmentTarget = (item, manualAdjustmentMode, direction) => {
  const snap = getRowAdjustmentSnapshot(item);
  const activeModes = snap.activeModes || [];

  if (activeModes.length === 0) {
    return direction === "increase"
      ? ADJUSTMENT_MODE.FIXEDAMOUNT
      : ADJUSTMENT_MODE.DISCOUNT;
  }

  if (activeModes.length === 1) {
    return activeModes[0];
  }

  if (activeModes.includes(manualAdjustmentMode)) {
    return manualAdjustmentMode;
  }

  return direction === "increase"
    ? ADJUSTMENT_MODE.FIXEDAMOUNT
    : ADJUSTMENT_MODE.DISCOUNT;
};

const applyIncreaseOnRow = ({ idx, mode, addPretax, getValues, setValue }) => {
  const path = (f) => `stockEntries.${idx}.${f}`;

  const item = {
    makingPercentage: getValues(path("makingPercentage")),
    makingPerGram: getValues(path("makingPerGram")),
    fixedWastage: getValues(path("fixedWastage")),
    fixedAmount: getValues(path("fixedAmount")),
    netWt: getValues(path("netWt")),
    ratePerGram: getValues(path("ratePerGram")),
  };

  const snap = getRowAdjustmentSnapshot(item);

  if (mode === ADJUSTMENT_MODE.MAKING) {
    const base = round2(snap.netWt * snap.ratePerGram);
    if (base <= 0) {
      const currentFixed = num(getValues(path("fixedAmount")));
      setValue(path("fixedAmount"), round2(currentFixed + addPretax), {
        shouldDirty: true,
      });
      return;
    }

    const addPct = round2((addPretax / base) * 100);
    setValue(path("makingPercentage"), round2(snap.makingPercentage + addPct), {
      shouldDirty: true,
    });
    return;
  }

  if (mode === ADJUSTMENT_MODE.MAKINGPERGRAM) {
    if (snap.netWt <= 0) {
      const currentFixed = num(getValues(path("fixedAmount")));
      setValue(path("fixedAmount"), round2(currentFixed + addPretax), {
        shouldDirty: true,
      });
      return;
    }

    const addPerGram = round2(addPretax / snap.netWt);
    setValue(path("makingPerGram"), round2(snap.makingPerGram + addPerGram), {
      shouldDirty: true,
    });
    return;
  }

  if (mode === ADJUSTMENT_MODE.FIXEDWASTAGE) {
    if (snap.ratePerGram <= 0) {
      const currentFixed = num(getValues(path("fixedAmount")));
      setValue(path("fixedAmount"), round2(currentFixed + addPretax), {
        shouldDirty: true,
      });
      return;
    }

    const addWt = round3(addPretax / snap.ratePerGram);
    setValue(path("fixedWastage"), round3(snap.fixedWastage + addWt), {
      shouldDirty: true,
    });
    return;
  }

  const currentFixed = num(getValues(path("fixedAmount")));
  setValue(path("fixedAmount"), round2(currentFixed + addPretax), {
    shouldDirty: true,
  });
};

const applyReductionOnRow = ({
  idx,
  mode,
  reducePretax,
  getValues,
  setValue,
}) => {
  const path = (f) => `stockEntries.${idx}.${f}`;

  const item = {
    makingPercentage: getValues(path("makingPercentage")),
    makingPerGram: getValues(path("makingPerGram")),
    fixedWastage: getValues(path("fixedWastage")),
    fixedAmount: getValues(path("fixedAmount")),
    netWt: getValues(path("netWt")),
    ratePerGram: getValues(path("ratePerGram")),
  };

  const snap = getRowAdjustmentSnapshot(item);

  if (mode === ADJUSTMENT_MODE.DISCOUNT) {
    return round2(reducePretax);
  }

  if (mode === ADJUSTMENT_MODE.MAKING) {
    const consumable = Math.min(snap.makingPctAmount, reducePretax);
    const base = round2(snap.netWt * snap.ratePerGram);

    if (base > 0) {
      const newAmount = round2(snap.makingPctAmount - consumable);
      const newPct = round2((newAmount / base) * 100);
      setValue(path("makingPercentage"), newPct, { shouldDirty: true });
    }

    return round2(reducePretax - consumable);
  }

  if (mode === ADJUSTMENT_MODE.MAKINGPERGRAM) {
    const consumable = Math.min(snap.makingPerGramAmount, reducePretax);

    if (snap.netWt > 0) {
      const newAmount = round2(snap.makingPerGramAmount - consumable);
      const newPerGram = round2(newAmount / snap.netWt);
      setValue(path("makingPerGram"), newPerGram, { shouldDirty: true });
    }

    return round2(reducePretax - consumable);
  }

  if (mode === ADJUSTMENT_MODE.FIXEDWASTAGE) {
    const consumable = Math.min(snap.fixedWastageAmount, reducePretax);

    if (snap.ratePerGram > 0) {
      const newAmount = round2(snap.fixedWastageAmount - consumable);
      const newWt = round3(newAmount / snap.ratePerGram);
      setValue(path("fixedWastage"), newWt, { shouldDirty: true });
    }

    return round2(reducePretax - consumable);
  }

  const consumable = Math.min(snap.fixedAmountAmount, reducePretax);
  const newFixed = round2(snap.fixedAmount - consumable);
  setValue(path("fixedAmount"), newFixed, { shouldDirty: true });

  return round2(reducePretax - consumable);
};

const isTdsEligibleAuto = (customerInfo) => !!customerInfo?.tdsApplicable;

const EstimateForm = ({
  existingData = null,
  mode = "create",
  itemId = null,
  allowMultipleRows = true,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [addEstimate, addState] = useAddEstimateMutation();
  const [updateEstimate, updateState] = useUpdateEstimateMutation();
  const [downloadEstimateReceiptPdf] = useDownloadEstimateReceiptPdfMutation();
  const {
    data: selRes,
    isLoading: selLoading,
    isFetching: selFetching,
    error: selError,
    refetch: refetchSelected,
  } = useGetTenantEstimateTemplateQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const { data: settingResp } = useGetOtherSettingsQuery();

  const ornamentsListRef = useRef(null);
  const looseStoneListRef = useRef(null);
  const looseDiamondListRef = useRef(null);
  const rowFirstRefs = useRef({});
  const limit = 2;
  const [customerSearch, setCustomerSearch] = useState("");
  const [quotSearch, setQuotSearch] = useState("");
  const debounceCustomerSearch = useDebounce(customerSearch, 1000);
  const debounceQuotSearch = useDebounce(quotSearch, 1000);
  const prevBranchIdRef = useRef(null);
  const [quickOpen, setQuickOpen] = useState(false);

  const [tinyArgs, setTinyArgs] = useState(null);
  const [itemType, setItemType] = useState("ornaments");
  const [bulkRate, setBulkRate] = useState("");
  const [bulkHallmark, setBulkHallmark] = useState("");

  const [sumOpen, setSumOpen] = useState(false);
  const authUser = useSelector((s) => s.auth?.user);

  const summaryRef = useRef(null);
  const lastCreatedRef = useRef(null);
  const customerSelectRef = useRef(null);

  // ✅ Print confirm dialog state
  const [printPrompt, setPrintPrompt] = useState({
    open: false,
    invoiceId: null,
  });
  const [estimatePreview, setEstimatePreview] = useState({
    open: false,
    html: "",
    loading: false,
  });

  const categoriesFromState = useSelector((s) => s.categories?.categories);
  const productsFromState = useSelector((s) => s.products?.products);
  const designFromState = useSelector((s) => s.designs?.designs);
  const purityFromState = useSelector((s) => s.purities?.purities);
  const branchesFromState = useSelector((s) => s.branches?.branches);
  const lastestRates = useSelector((s) => s.lastestRates);

  const {
    control,
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors },
    getValues,
    watch,
    trigger,
    clearErrors,
    unregister,
  } = useForm({
    shouldUnregister: false,
    defaultValues: existingData || DEFAULTS,
  });

  // ✅ Branch & invoice date watch (top, so hooks below use it)
  const branchSel = watch("branch");
  const invoiceDate = watch("invoiceDate");
  const quotationNumber = watch("quotationNumber");

  // ✅ Branch ko sabse pehle lane ke liye sort
  const topFields = useMemo(() => {
    if (!InvoiceTopFields) return [];
    const arr = [...InvoiceTopFields];
    arr.sort((a, b) => {
      if (a.name === "branch" && b.name !== "branch") return -1;
      if (b.name === "branch" && a.name !== "branch") return 1;
      return 0;
    });
    return arr;
  }, []);

  // ✅ Customer list branch-wise filter
  const {
    data: customerResp,
    isFetching: loadingCustomers,
    refetch: refetchCustomers,
  } = useGetAllCustomersQuery(
    {
      page: 1,
      limit: limit,
      sortOrder: "desc",
      sortBy: "createdAt",
      search: debounceCustomerSearch,
      branchId: branchSel?.value || undefined, // ✅ filter by branch
    },
    {
      skip: !branchSel?.value, // ✅ branch select hone ke baad hi hit kare
    },
  );

  // ✅ Customer list branch-wise filter
  const {
    data: quotResp,
    isFetching: quotLoading,
    refetch: refetchQuatation,
  } = useGetQuotationsQuery(
    {
      page: 1,
      limit: limit,
      sortOrder: "desc",
      sortBy: "createdAt",
      status: "pending",
      search: debounceQuotSearch,
      branchId: branchSel?.value || undefined, // ✅ filter by branch
    },
    {
      skip: !branchSel?.value, // ✅ branch select hone ke baad hi hit kare
    },
  );
  // Tiny fetch for freshly created customer
  const { data: tinyResp } = useGetAllCustomersQuery(tinyArgs, {
    skip: !tinyArgs,
  });

  useEffect(() => {
    let bid = authUser?.employeeDetails?.branch?._id;
    let cid = authUser?.employeeDetails?.counter?._id;
    let bname = authUser?.employeeDetails?.branch?.branchName;
    let cname = authUser?.employeeDetails?.counter?.counterName;
    if (mode != "edit" && authUser?.role == "staff" && bid) {
      setValue("branch", { value: bid, label: bname });
    }
  }, [authUser, mode]);

  useEffect(() => {
    if (!tinyArgs || !tinyResp?.data) return;
    const created = lastCreatedRef.current;
    const newest =
      tinyResp.data.find((c) => String(c?._id) === String(created?._id)) ||
      tinyResp.data[0];
    if (newest) {
      const opt =
        (handleDropDowns({ key: "customer", customers: [newest] }) || [])[0] ||
        null;
      if (opt) onCustomerPicked(opt);
    }
    setTinyArgs(null);
  }, [tinyResp, tinyArgs]);

  const customers = customerResp?.data || [];
  const quotations = quotResp?.rows || [];

  // hydrate form when existingData comes (EDIT)
  useEffect(() => {
    if (existingData && mode === "edit") {
      reset((prev) => {
        const prevJson = JSON.stringify(prev);
        const nextJson = JSON.stringify(existingData);
        if (prevJson === nextJson) return prev;
        return existingData;
      });

      const hasOrn = (existingData.stockEntries || []).length > 0;
      const hasStn = (existingData.looseStones || []).length > 0;
      const hasDia = (existingData.looseDiamonds || []).length > 0;
      if (hasOrn) setItemType("ornaments");
      else if (hasStn) setItemType("loose");
      else if (hasDia) setItemType("looseDiamond");
    }
  }, [existingData, mode, reset]);

  const {
    fields: ornamentFields,
    append: ornamentAppend,
    remove: ornamentRemove,
  } = useFieldArray({ control, name: "stockEntries" });

  const {
    fields: looseFields,
    append: looseAppend,
    remove: looseRemove,
  } = useFieldArray({ control, name: "looseStones" });

  const {
    fields: looseDiaFields,
    append: looseDiaAppend,
    remove: looseDiaRemove,
  } = useFieldArray({ control, name: "looseDiamonds" });

  const customer = watch("customer");
  const stockEntries = watch("stockEntries");

  const {
    fields: payFields,
    append: payAppend,
    remove: payRemove,
  } = useFieldArray({ control, name: "payments" });

  const [payMode, setPayMode] = useState("Cash");
  const [payAmt, setPayAmt] = useState("");
  const [payRef, setPayRef] = useState("");
  const [payDesc, setPayDesc] = useState("");

  const entries = useWatch({ control, name: "stockEntries" }) || [];
  const looseArr = useWatch({ control, name: "looseStones" }) || [];
  const looseDiaArr = useWatch({ control, name: "looseDiamonds" }) || [];
  const payments = useWatch({ control, name: "payments" }) || [];

  // ✅ Branch change pe items + customer clear
  useEffect(() => {
    const currentBranchId = branchSel?.value || null;
    if (prevBranchIdRef.current === null) {
      prevBranchIdRef.current = currentBranchId;
      return;
    }
    if (prevBranchIdRef.current !== currentBranchId) {
      setValue("stockEntries", []);
      setValue("looseStones", []);
      setValue("looseDiamonds", []);
      setValue("customer", null); // ✅ branch change -> customer reset
      setCustomerSearch("");
      setQuotSearch("");
    }
    prevBranchIdRef.current = currentBranchId;
  }, [branchSel?.value, setValue]);

  const { data: lastNoResp } = useGetLastEstimateNumberQuery(
    branchSel?.value && invoiceDate
      ? { branchId: branchSel.value, date: invoiceDate }
      : { skipToken: true },
    { skip: mode === "edit" || !branchSel?.value || !invoiceDate },
  );

  const nextNumber = lastNoResp?.nextNumber;

  // ============================== AGGREGATE ==============================
  const agg = useMemo(() => {
    const out = {
      ornPretaxNonMrp: 0,
      ornMrpFinal: 0,
      stonePretaxNonMrp: 0,
      stoneMrpFinal: 0,
      stoneGstWeightedNumerator: 0,
      diaPretaxNonMrp: 0,
      diaMrpFinal: 0,
      diaGstWeightedNumerator: 0,
      totalFineWt: 0,
      fineOnlyWt: 0,
    };

    for (const it of entries) {
      const mrpVal = num(it?.mrp);
      const itemAmount = num(it?.itemAmount);
      const fineOnly = num(it?.fineWt);
      const finePlusW = num(it?.fineWastageWt);

      if (mrpVal > 0) out.ornMrpFinal += mrpVal;
      else out.ornPretaxNonMrp += itemAmount;

      if (fineOnly || finePlusW) {
        out.fineOnlyWt += fineOnly;
        out.totalFineWt += finePlusW || fineOnly;
      }
    }

    for (const ls of looseArr) {
      const mrpVal = num(ls?.mrp);
      const itemAmount = num(ls?.itemAmount);
      const gstRate = Math.max(0, num(ls?.gstRate));

      if (mrpVal > 0) out.stoneMrpFinal += mrpVal;
      else {
        out.stonePretaxNonMrp += itemAmount;
        out.stoneGstWeightedNumerator += itemAmount * gstRate;
      }
    }

    for (const d of looseDiaArr) {
      const mrpVal = num(d?.mrp);
      const itemAmount = num(d?.itemAmount);
      const gstRate = Math.max(0, num(d?.gstRate));

      if (mrpVal > 0) out.diaMrpFinal += mrpVal;
      else {
        out.diaPretaxNonMrp += itemAmount;
        out.diaGstWeightedNumerator += itemAmount * gstRate;
      }
    }

    out.ornPretaxNonMrp = +out.ornPretaxNonMrp.toFixed(2);
    out.ornMrpFinal = +out.ornMrpFinal.toFixed(2);

    out.stonePretaxNonMrp = +out.stonePretaxNonMrp.toFixed(2);
    out.stoneMrpFinal = +out.stoneMrpFinal.toFixed(2);
    out.stoneGstWeightedNumerator = +out.stoneGstWeightedNumerator.toFixed(4);

    out.diaPretaxNonMrp = +out.diaPretaxNonMrp.toFixed(2);
    out.diaMrpFinal = +out.diaMrpFinal.toFixed(2);
    out.diaGstWeightedNumerator = +out.diaGstWeightedNumerator.toFixed(4);

    out.totalFineWt = +out.totalFineWt.toFixed(3);
    out.fineOnlyWt = +out.fineOnlyWt.toFixed(3);

    return out;
  }, [entries, looseArr, looseDiaArr]);
  // ======================================================================

  const ornSummary = useMemo(() => {
    let rows = entries.length;
    let qty = 0,
      pieces = 0,
      stonePcs = 0,
      diamondPcs = 0,
      totalWt = 0,
      packing = 0,
      gross = 0,
      stoneWt = 0,
      diamondGram = 0,
      netWt = 0,
      stoneAmt = 0,
      diamondAmt = 0,
      hallmarkAmt = 0,
      itemAmt = 0,
      itemGstAmt = 0,
      finalAmt = 0,
      mrp = 0;

    for (const it of entries) {
      qty += num(it?.qty);
      pieces += num(it?.pieces);
      stonePcs += num(it?.stonePcs);
      diamondPcs += num(it?.diamondPcs);

      const tw = num(it?.totalWt);
      const pw = num(it?.packingWt);
      const stw = num(it?.stoneWt);
      const dCt = num(it?.diamondWt);
      const dGm = +(dCt * 0.2);

      totalWt += tw;
      packing += pw;
      gross += +(tw - pw);
      stoneWt += stw;
      diamondGram += dGm;

      const nwt = +(tw - pw - stw - dGm);
      netWt += nwt;

      stoneAmt += num(it?.stoneAmount);
      diamondAmt += num(it?.diamondAmount);
      hallmarkAmt += num(it?.hallmarkAmount);
      itemAmt += num(it?.itemAmount);
      itemGstAmt += num(it?.itemGstAmount);
      finalAmt += num(it?.totalItemAmount);
      mrp += num(it?.mrp);
    }

    return {
      rows,
      qty: Math.round(qty),
      pieces: Math.round(pieces),
      stonePcs: Math.round(stonePcs),
      diamondPcs: Math.round(diamondPcs),
      totalWt: +totalWt.toFixed(3),
      packingWt: +packing.toFixed(3),
      grossWt: +gross.toFixed(3),
      stoneWt: +stoneWt.toFixed(3),
      diamondGram: +diamondGram.toFixed(3),
      netWt: +netWt.toFixed(3),
      stoneAmt: +stoneAmt.toFixed(2),
      diamondAmt: +diamondAmt.toFixed(2),
      hallmarkAmt: +hallmarkAmt.toFixed(2),
      itemAmt: +itemAmt.toFixed(2),
      itemGstAmt: +itemGstAmt.toFixed(2),
      finalAmt: +finalAmt.toFixed(2),
      mrp: +mrp.toFixed(2),
    };
  }, [entries]);

  useEffect(() => {
    const onDoc = (e) => {
      if (summaryRef.current && !summaryRef.current.contains(e.target)) {
        setSumOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setSumOpen(false);
    if (sumOpen) {
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onEsc);
    }
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [sumOpen]);

  const applyInvoiceDiscountItemWise = () => {
    const items = getValues("stockEntries") || [];
    if (!items.length) return;

    const totalInvDiscount = num(getValues("totalDiscount"));

    const nonMrp = items?.map((it, idx) => ({ it, idx }));
    // .filter((x) => num(x.it?.mrp) <= 0);

    const totalPreTax = nonMrp.reduce((s, x) => s + num(x.it?.itemAmount), 0);

    // 🔹 NO DISCOUNT CASE
    // if (invoiceDiscount <= 0 || totalPreTax <= 0) {
    //   items.forEach((it, i) => {
    //     if (num(it?.mrp) > 0) return;

    //     const base = num(it?.itemAmount);
    //     const gstAmt = +((base * ORNAMENT_GST) / 100).toFixed(2);

    //     setValue(`stockEntries.${i}.itemDiscountAmount`, 0);
    //     setValue(`stockEntries.${i}.itemAmountAfterDiscount`, base);
    //     setValue(`stockEntries.${i}.itemGstAmount`, gstAmt);
    //     setValue(
    //       `stockEntries.${i}.totalItemAmount`,
    //       +(base + gstAmt).toFixed(2),
    //     );
    //   });
    //   return;
    // }

    if (totalInvDiscount === 0 || totalPreTax <= 0) {
      items.forEach((it, i) => {
        const mrpVal = num(it?.mrp);

        if (mrpVal > 0) {
          const taxable = +(mrpVal / (1 + ORNAMENT_GST / 100)).toFixed(2);
          const gstAmt = +((taxable * ORNAMENT_GST) / 100).toFixed(2);

          setValue(`stockEntries.${i}.itemDiscountAmount`, 0);
          setValue(`stockEntries.${i}.itemAmountAfterDiscount`, taxable);
          setValue(`stockEntries.${i}.itemGstAmount`, gstAmt);
          setValue(`stockEntries.${i}.totalItemAmount`, mrpVal);

          return;
        }

        const base = num(it?.itemAmount);
        const gstAmt = +((base * ORNAMENT_GST) / 100).toFixed(2);

        setValue(`stockEntries.${i}.itemDiscountAmount`, 0);
        setValue(`stockEntries.${i}.itemAmountAfterDiscount`, base);
        setValue(`stockEntries.${i}.itemGstAmount`, gstAmt);
        setValue(
          `stockEntries.${i}.totalItemAmount`,
          +(base + gstAmt).toFixed(2),
        );
      });

      return;
    }

    // 🔹 DISCOUNT CASE
    nonMrp.forEach(({ it, idx }) => {
      const preTax = num(it.itemAmount);
      const share = preTax / totalPreTax;

      const disc = +(totalInvDiscount * share).toFixed(2);
      const after = +(preTax - disc).toFixed(2);

      const gstAmt = +((after * ORNAMENT_GST) / 100).toFixed(2);
      const finalAmt = +(after + gstAmt).toFixed(2);

      setValue(`stockEntries.${idx}.itemDiscountAmount`, disc);
      setValue(`stockEntries.${idx}.itemAmountAfterDiscount`, after);
      setValue(`stockEntries.${idx}.itemGstAmount`, gstAmt);
      setValue(`stockEntries.${idx}.totalItemAmount`, finalAmt);
    });
  };

  // ================== DISCOUNT & GST SPLIT ==================
  const discount = num(watch("totalDiscount"));
  const courierCharge = num(watch("courierCharge"));
  const selectedBranch = branchSel; // ✅ same watch reuse

  const preTaxTotalNonMrp = +(
    entries.reduce((s, it) => s + Number(it?.itemAmountAfterDiscount || 0), 0) +
    looseArr.reduce(
      (s, it) => s + Number(it?.itemAmountAfterDiscount || 0),
      0,
    ) +
    looseDiaArr.reduce(
      (s, it) => s + Number(it?.itemAmountAfterDiscount || 0),
      0,
    )
  ).toFixed(2);
  const taxableAmount = +(
    entries.reduce((s, it) => s + Number(it?.itemAmount || 0), 0) +
    looseArr.reduce((s, it) => s + Number(it?.itemAmount || 0), 0) +
    looseDiaArr.reduce((s, it) => s + Number(it?.itemAmount || 0), 0)
  ).toFixed(2);

  // const effectiveDiscount = Math.min(discount, preTaxTotalNonMrp);

  // const share = (bucket) =>
  //   preTaxTotalNonMrp > 0 ? bucket / preTaxTotalNonMrp : 0;

  // const discountOrn = +(effectiveDiscount * share(agg.ornPretaxNonMrp)).toFixed(
  //   2,
  // );
  // const discountStone = +(
  //   effectiveDiscount * share(agg.stonePretaxNonMrp)
  // ).toFixed(2);
  // const discountDia = +(
  //   effectiveDiscount -
  //   discountOrn -
  //   discountStone
  // ).toFixed(2);

  // const ornAfterDiscount = +(agg.ornPretaxNonMrp - discountOrn).toFixed(2);
  // const stoneAfterDiscount = +(agg.stonePretaxNonMrp - discountStone).toFixed(
  // 2,
  // );
  // const diaAfterDiscount = +(agg.diaPretaxNonMrp - discountDia).toFixed(2);
  const salePreTax = preTaxTotalNonMrp;
  const gstOrnRate = ORNAMENT_GST;
  const gstLooseWeightedRate =
    agg.stonePretaxNonMrp > 0
      ? +(agg.stoneGstWeightedNumerator / agg.stonePretaxNonMrp).toFixed(4)
      : 0;

  const gstDiaWeightedRate =
    agg.diaPretaxNonMrp > 0
      ? +(agg.diaGstWeightedNumerator / agg.diaPretaxNonMrp).toFixed(4)
      : 0;

  const gstOrn = +entries
    .reduce((sum, it) => sum + Number(it?.itemGstAmount || 0), 0)
    .toFixed(2);

  const gstLoose = +looseArr
    .reduce((sum, it) => sum + Number(it?.itemGstAmount || 0), 0)
    .toFixed(2);

  const gstDia = +looseDiaArr
    .reduce((sum, it) => sum + Number(it?.itemGstAmount || 0), 0)
    .toFixed(2);

  const subTotalNoCourier = +(
    ornSummary.finalAmt +
    agg.stoneMrpFinal +
    agg.diaMrpFinal
  ).toFixed(2);

  const computedGrandTotal = +(subTotalNoCourier + courierCharge).toFixed(2);

  const tdsApply = !!watch("tdsApply");
  const tdsBase = salePreTax;

  const tdsAmount = tdsApply ? +((tdsBase * tds) / 100).toFixed(2) : 0;

  const totalPayable = +(computedGrandTotal + tdsAmount).toFixed(2);

  const paid = +payments.reduce((s, p) => s + num(p?.amount), 0).toFixed(2);

  const paidFineMetal = +(
    payments?.reduce((s, p) => s + num(p?.fineMetalWt), 0) || 0
  ).toFixed(3);
  const balanceFineMetal = +(agg.totalFineWt - paidFineMetal).toFixed(3);
  const balanceNet = +(totalPayable - paid).toFixed(2);

  const grandTotalManual = watch("grandTotalManual");
  const manualAdjustmentMode =
    settingResp?.data?.manualAdjustmentMode || "DISCOUNT";
  // =========================================================
  const totalDiscount = watch("totalDiscount");

  useEffect(() => {
    applyInvoiceDiscountItemWise();
  }, [
    totalDiscount,
    entries.length,
    JSON.stringify(
      entries.map((e) => ({
        itemAmount: e.itemAmount,
        mrp: e.mrp,
      })),
    ),
  ]);
  const registerFirstField = (id, el) => {
    if (el) rowFirstRefs.current[id] = el;
  };
  const focusRowAt = (listRef, idx) => {
    const cards = listRef.current?.querySelectorAll(".invoice-card");
    if (!cards || !cards.length) return;
    const i = Math.max(0, Math.min(idx, cards.length - 1));
    const card = cards[i];
    card?.scrollIntoView({ behavior: "smooth", block: "start" });
    const id = card?.dataset?.fieldId;
    const el = rowFirstRefs.current[id];
    if (el?.focus) el.focus();
  };
  const getFirstErrorRowIndex = (arrayName) => {
    const se = errors?.[arrayName];
    if (!se) return -1;
    const rows = Object.keys(se)
      .map((k) => Number(k))
      .sort((a, b) => a - b);
    return rows.length ? rows[0] : -1;
  };

  const getValidationPathsForActiveTab = () => {
    if (itemType === "loose") {
      const i = Math.max(0, (looseFields?.length || 1) - 1);
      if (!looseFields?.length) return [];
      return [`looseStones.${i}.counterRow`, `looseStones.${i}.stnPopupName`];
    } else if (itemType === "looseDiamond") {
      const i = Math.max(0, (looseDiaFields?.length || 1) - 1);
      if (!looseDiaFields?.length) return [];
      return [`looseDiamonds.${i}.counterRow`, `looseDiamonds.${i}.diaName`];
    }
    const i = Math.max(0, (ornamentFields?.length || 1) - 1);
    if (!ornamentFields?.length) return [];
    return [
      `stockEntries.${i}.counterRow`,
      `stockEntries.${i}.category`,
      `stockEntries.${i}.product`,
      `stockEntries.${i}.design`,
      `stockEntries.${i}.purity`,
      `stockEntries.${i}.totalWt`,
      `stockEntries.${i}.grossWt`,
      `stockEntries.${i}.netWt`,
      `stockEntries.${i}.qty`,
    ];
  };

  // ================== Keyboard shortcuts (Ctrl + + & Ctrl + S) ==================
  useEffect(() => {
    const onKeys = (e) => {
      // ✅ Ctrl+S / Cmd+S -> Save/Update invoice
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        if (!addState.isLoading && !updateState.isLoading) {
          handleSubmit(onSubmit)();
        }
        return;
      }

      // Add-row shortcut only for create + multi-row
      if (mode === "edit" || !allowMultipleRows) return;

      if (
        e.ctrlKey &&
        (e.key === "+" || e.key === "=" || e.code === "NumpadAdd")
      ) {
        e.preventDefault();

        const arrayName =
          itemType === "loose"
            ? "looseStones"
            : itemType === "looseDiamond"
              ? "looseDiamonds"
              : "stockEntries";

        const currentPaths = getValidationPathsForActiveTab();

        Promise.resolve(trigger(currentPaths)).then((valid) => {
          if (!valid) {
            setTimeout(() => {
              const badIdx = getFirstErrorRowIndex(arrayName);
              if (badIdx >= 0) {
                const ref =
                  itemType === "loose"
                    ? looseStoneListRef
                    : itemType === "looseDiamond"
                      ? looseDiamondListRef
                      : ornamentsListRef;
                focusRowAt(ref, badIdx);
              }
            }, 0);
            return;
          }

          if (itemType === "loose") {
            const nextIdx = looseFields.length;
            looseAppend({}, { shouldFocus: false });
            requestAnimationFrame(() =>
              setTimeout(() => focusRowAt(looseStoneListRef, nextIdx), 0),
            );
          } else if (itemType === "looseDiamond") {
            const nextIdx = looseDiaFields.length;
            looseDiaAppend({}, { shouldFocus: false });
            requestAnimationFrame(() =>
              setTimeout(() => focusRowAt(looseDiamondListRef, nextIdx), 0),
            );
          } else {
            const nextIdx = ornamentFields.length;
            ornamentAppend(
              {
                totalWt: "",
                stones: [defaultStoneFields],
                diamonds: [defaultDiamondFields],
              },
              { shouldFocus: false },
            );
            requestAnimationFrame(() =>
              setTimeout(() => focusRowAt(ornamentsListRef, nextIdx), 0),
            );
          }
        });
      }
    };

    window.addEventListener("keydown", onKeys);
    return () => window.removeEventListener("keydown", onKeys);
  }, [
    ornamentAppend,
    looseAppend,
    looseDiaAppend,
    ornamentFields.length,
    looseFields.length,
    looseDiaFields.length,
    mode,
    allowMultipleRows,
    trigger,
    errors,
    itemType,
    handleSubmit,
    addState.isLoading,
    updateState.isLoading,
  ]);
  // =====================================================================

  // ✅ Customer select pe focus helper (print ke baad)
  const focusCustomerSelect = () => {
    if (customerSelectRef.current && customerSelectRef.current.focus) {
      customerSelectRef.current.focus();
    }
  };

  const handleAddRow = async () => {
    if (mode === "edit" || !allowMultipleRows) return;

    const arrayName =
      itemType === "loose"
        ? "looseStones"
        : itemType === "looseDiamond"
          ? "looseDiamonds"
          : "stockEntries";

    const currentPaths = getValidationPathsForActiveTab();
    const valid = await trigger(currentPaths);

    if (!valid) {
      const badIdx = getFirstErrorRowIndex(arrayName);
      if (badIdx >= 0) {
        const ref =
          itemType === "loose"
            ? looseStoneListRef
            : itemType === "looseDiamond"
              ? looseDiamondListRef
              : ornamentsListRef;
        focusRowAt(ref, badIdx);
      }
      return;
    }

    if (itemType === "loose") {
      const nextIdx = looseFields.length;
      looseAppend({}, { shouldFocus: false });
      requestAnimationFrame(() =>
        setTimeout(() => focusRowAt(looseStoneListRef, nextIdx), 0),
      );
    } else if (itemType === "looseDiamond") {
      const nextIdx = looseDiaFields.length;
      looseDiaAppend({}, { shouldFocus: false });
      requestAnimationFrame(() =>
        setTimeout(() => focusRowAt(looseDiamondListRef, nextIdx), 0),
      );
    } else {
      const nextIdx = ornamentFields.length;
      ornamentAppend(
        {
          totalWt: "",
          stones: [defaultStoneFields],
          diamonds: [defaultDiamondFields],
        },
        { shouldFocus: false },
      );
      requestAnimationFrame(() =>
        setTimeout(() => focusRowAt(ornamentsListRef, nextIdx), 0),
      );
    }
  };

  const onCustomerPicked = (picked) => {
    setValue("customer", picked ?? null);
    const info = picked?.info;
    if (info) {
      setValue("fineGold", threeDecimal(info.fineGold));
      setValue("fineSilver", threeDecimal(info.fineSilver));
      setValue("advanceAmount", threeDecimal(info.advanceAmount));
      setValue("balanceAmount", threeDecimal(info.balanceAmount));
      if (typeof watch("tdsApply") === "undefined") {
        setValue("tdsApply", isTdsEligibleAuto(info));
      }
    }
  };

  const onQuotationPicked = (picked) => {
    setValue("quotationNumber", picked ?? null);
  };

  const addPayment = () => {
    const amt = parseFloat(payAmt);
    if (!Number.isFinite(amt) || amt <= 0) {
      return toast.error("Enter valid amount");
    }

    const currentPaid = payments.reduce((s, p) => s + num(p?.amount), 0);
    const currentOutstanding = +(totalPayable - currentPaid).toFixed(2);

    if (amt > currentOutstanding + 0.0001) {
      return toast.error("Amount exceeds outstanding.");
    }

    if (payMode === "Wallet") {
      const wallet = num(customer?.info?.balanceAmount);
      if (!customer?.value) {
        return toast.error("Pick a customer before using Wallet.");
      }
      if (wallet <= 0) {
        return toast.error("Wallet balance is 0. Can't add wallet payment.");
      }
      if (amt > wallet + 0.0001) {
        return toast.error(`Wallet balance ₹${wallet.toFixed(2)}.`);
      }
    }

    payAppend({
      type: "Receipt",
      mode: payMode,
      amount: Number(amt).toFixed(2),
      refNo: payRef || "",
      description: payDesc || "",
      sourceType: "ESTIMATE",
    });
    setPayAmt("");
    setPayRef("");
    setPayDesc("");
  };

  const recalcItemRow = (i) => {
    const e = (f) => `stockEntries.${i}.${f}`;
    const get = (k) => Number(getValues(e(k)) || 0);

    const totalWt = get("totalWt");
    const packingWt = get("packingWt");
    const stoneWt = get("stoneWt");
    const diamondCt = get("diamondWt");
    const ratePerGram = get("ratePerGram");
    const finePercent = get("finePercentage");
    const makingPct = get("makingPercentage");
    const makingPerGram = get("makingPerGram");
    const fixedMaking = get("fixedWastage");
    const fixedAmount = get("fixedAmount");
    const stoneAmount = get("stoneAmount");
    const diamondAmount = get("diamondAmount");
    const hallmarkAmount = get("hallmarkAmount");
    const itemDisc = get("itemDiscountAmount");

    const grossWt = +(totalWt - packingWt).toFixed(3);
    const diamondGram = +(diamondCt * 0.2).toFixed(3);
    const netWt = +(grossWt - stoneWt - diamondGram).toFixed(3);
    const fineWt = +((finePercent / 100) * netWt).toFixed(3);
    const makingBaseWt = netWt;
    const wastageWt = +((makingPct / 100) * makingBaseWt + fixedMaking).toFixed(
      3,
    );
    const fineWastageWt = +(fineWt + wastageWt).toFixed(3);

    setValue(e("grossWt"), grossWt);
    setValue(e("netWt"), netWt);
    setValue(e("fineWt"), fineWt);
    setValue(e("wastageWt"), wastageWt);
    setValue(e("fineWastageWt"), fineWastageWt);

    const metalAmount = +(ratePerGram * netWt).toFixed(2);
    const amtByFixedMaking = +(fixedMaking * ratePerGram).toFixed(2);
    const amtByMakingPerGram = +(makingPerGram * netWt).toFixed(2);
    const amtByPercentage = +(
      (makingBaseWt * ratePerGram * makingPct) /
      100
    ).toFixed(2);

    const preTaxMaking = +(
      amtByMakingPerGram +
      amtByPercentage +
      amtByFixedMaking +
      fixedAmount
    ).toFixed(2);
    const itemAmount = +(
      metalAmount +
      preTaxMaking +
      stoneAmount +
      diamondAmount +
      hallmarkAmount
    ).toFixed(2);

    const mrpVal = Number(getValues(e("mrp")) || 0);
    // if (mrpVal > 0) {
    //   setValue(e("itemAmount"), itemAmount);
    //   setValue(e("itemGstAmount"), 0);
    //   setValue(e("totalItemAmount"), +Number(mrpVal).toFixed(2));
    //   return;
    // }

    const itemGstAmount = +((itemAmount * ORNAMENT_GST) / 100).toFixed(2);
    const totalItemAmount = +(itemAmount + itemGstAmount - itemDisc).toFixed(2);

    setValue(e("itemAmount"), itemAmount);
    setValue(e("itemGstAmount"), itemGstAmount);
    setValue(e("totalItemAmount"), totalItemAmount);
  };

  const applyAdaptiveIncreaseItemWise = (targetManualTotal) => {
    const items = getValues("stockEntries") || [];
    if (!items.length) return;

    const eligible = items
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => num(it?.mrp) <= 0);

    if (!eligible.length) {
      toast.error("Only MRP items present. Increase not possible here.");
      setValue("grandTotalManual", "");
      return;
    }

    const currentComputed = computedGrandTotal;
    const courier = courierCharge || 0;

    const currentNoCourier = round2(currentComputed - courier);
    const targetNoCourier = round2(targetManualTotal - courier);

    if (targetNoCourier <= currentNoCourier + 0.001) {
      setValue("grandTotalManual", "");
      return;
    }

    // ✅ estimate me jaisa chal raha hai waise hi rakh rahe — invoice jaisa behaviour only
    const pretaxIncrease = round2(targetNoCourier - currentNoCourier);

    const totalBase = eligible.reduce(
      (s, { it }) =>
        s + Math.max(num(it?.itemAmountAfterDiscount || it?.itemAmount), 0),
      0,
    );

    eligible.forEach(({ it, idx }, arrIdx) => {
      const base = Math.max(
        num(it?.itemAmountAfterDiscount || it?.itemAmount),
        0,
      );
      const share = totalBase > 0 ? base / totalBase : 1 / eligible.length;

      const usedBefore = eligible.slice(0, arrIdx).reduce((sum, x) => {
        const prevBase = Math.max(
          num(x.it?.itemAmountAfterDiscount || x.it?.itemAmount),
          0,
        );
        const prevShare =
          totalBase > 0 ? prevBase / totalBase : 1 / eligible.length;
        return sum + round2(pretaxIncrease * prevShare);
      }, 0);

      const rowIncrease =
        arrIdx === eligible.length - 1
          ? round2(pretaxIncrease - usedBefore)
          : round2(pretaxIncrease * share);

      const targetMode = resolveRowAdjustmentTarget(
        it,
        manualAdjustmentMode,
        "increase",
      );

      applyIncreaseOnRow({
        idx,
        mode: targetMode,
        addPretax: rowIncrease,
        getValues,
        setValue,
      });
    });

    setValue("totalDiscount", 0, { shouldDirty: true });

    items.forEach((_, i) => {
      recalcItemRow(i);
    });

    setValue("grandTotalManual", "");
  };

  const applyAdaptiveReductionItemWise = (targetManualTotal) => {
    const items = getValues("stockEntries") || [];
    if (!items.length) return;

    const eligible = items
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => num(it?.mrp) <= 0);

    if (!eligible.length) {
      toast.error("Only MRP items present. Total can't be reduced here.");
      setValue("grandTotalManual", "");
      return;
    }

    const currentComputed = computedGrandTotal;
    const courier = courierCharge || 0;

    const currentNoCourier = round2(currentComputed - courier);
    const targetNoCourier = round2(targetManualTotal - courier);

    if (targetNoCourier >= currentNoCourier - 0.001) {
      setValue("grandTotalManual", "");
      return;
    }

    // ✅ estimate me direct reduction
    const pretaxReduction = round2(currentNoCourier - targetNoCourier);

    const totalBase = eligible.reduce(
      (s, { it }) =>
        s + Math.max(num(it?.itemAmountAfterDiscount || it?.itemAmount), 0),
      0,
    );

    let extraDiscountToApply = 0;

    eligible.forEach(({ it, idx }, arrIdx) => {
      const base = Math.max(
        num(it?.itemAmountAfterDiscount || it?.itemAmount),
        0,
      );
      const share = totalBase > 0 ? base / totalBase : 1 / eligible.length;

      const usedBefore = eligible.slice(0, arrIdx).reduce((sum, x) => {
        const prevBase = Math.max(
          num(x.it?.itemAmountAfterDiscount || x.it?.itemAmount),
          0,
        );
        const prevShare =
          totalBase > 0 ? prevBase / totalBase : 1 / eligible.length;
        return sum + round2(pretaxReduction * prevShare);
      }, 0);

      const rowReduction =
        arrIdx === eligible.length - 1
          ? round2(pretaxReduction - usedBefore)
          : round2(pretaxReduction * share);

      const targetMode = resolveRowAdjustmentTarget(
        it,
        manualAdjustmentMode,
        "decrease",
      );

      const leftover = applyReductionOnRow({
        idx,
        mode: targetMode,
        reducePretax: rowReduction,
        getValues,
        setValue,
      });

      extraDiscountToApply = round2(extraDiscountToApply + leftover);
    });

    items.forEach((_, i) => {
      recalcItemRow(i);
    });

    const currentDiscount = num(getValues("totalDiscount"));
    setValue("totalDiscount", round2(currentDiscount + extraDiscountToApply), {
      shouldDirty: true,
    });

    setValue("grandTotalManual", "");
  };

  const applyRateForAll = () => {
    const v = parseFloat(bulkRate);
    if (!Number.isFinite(v)) return;
    entries.forEach((_, i) => {
      setValue(`stockEntries.${i}.ratePerGram`, v);
      recalcItemRow(i);
    });
  };

  const applyHallmarkForAll = () => {
    const v = parseFloat(bulkHallmark);
    if (!Number.isFinite(v)) return;
    entries.forEach((_, i) => {
      setValue(`stockEntries.${i}.hallmarkAmount`, v);
      recalcItemRow(i);
    });
  };

  const handleGrandManualBlur = () => {
    const manual = parseFloat(grandTotalManual);

    if (Number.isNaN(manual)) {
      setValue("grandTotalManual", "");
      return;
    }

    const computedNoCourier = round2(computedGrandTotal - courierCharge);
    const manualNoCourier = round2(manual - courierCharge);

    if (manualNoCourier > computedNoCourier + 0.001) {
      applyAdaptiveIncreaseItemWise(manual);
      return;
    }

    if (manualNoCourier >= computedNoCourier - 0.001) {
      setValue("grandTotalManual", "");
      return;
    }

    applyAdaptiveReductionItemWise(manual);
  };
  // ------------------ Normalize payload ------------------
  const idOrValue = (v) =>
    v && typeof v === "object"
      ? (v.value ?? v._id ?? v.id ?? v.label ?? "")
      : (v ?? "");

  const normalizeStones = (list = []) =>
    (list || []).map((s) => ({
      ...s,
      shape: idOrValue(s.shape),
      size: idOrValue(s.size),
      color: idOrValue(s.color),
      settingType: idOrValue(s.settingType),
    }));

  const normalizeDiamonds = (list = []) =>
    (list || []).map((d) => ({
      ...d,
      shape: idOrValue(d.shape ?? d.diaPopupShape),
      size: idOrValue(d.size ?? d.diaPopupSize ?? d.diaPopupSieve),
      color: idOrValue(d.color ?? d.diaPopupColor),
      clarity: idOrValue(d.clarity ?? d.diaPopupClarity),
      pcs: Number(d.pcs ?? d.diaPopupPcs ?? 0),
      wt: Number(d.wt ?? d.diaPopupWt ?? 0),
      rateByWt: Number(d.rateByWt ?? d.diaPopupRateByWt ?? 0),
      rateByPcs: Number(d.rateByPcs ?? d.diaPopupRateByPcs ?? 0),
      amount: Number(d.amount ?? d.diaPopupAmt ?? 0),
      deduct: d.deduct ?? d.diaPopupDeduct ?? "",
      description: d.description ?? d.diaPopupDescription ?? "",
    }));

  const buildPayload = (form) => {
    const payload = { ...form };
    payload.invoiceDate =
      form?.invoiceDate || convertToDateInputFormat(getTodayDate());

    payload.payments = (form?.payments || []).map((p) => ({
      type: "Receipt",
      mode: p.mode,
      amount: String(p.amount ?? "0"),
      refNo: p.refNo || "",
      description: p.description || "",
      sourceType: "ESTIMATE",
    }));

    payload.looseStones = (form?.looseStones || []).filter(
      (item) => item?.stnPopupName,
    );
    payload.looseDiamonds = (form?.looseDiamonds || []).filter(
      (item) => item?.diaName,
    );

    payload.stockEntries = (form?.stockEntries || [])
      .filter((item) => item?.category?.value)
      .map((it) => ({
        ...it,
        stones: normalizeStones(it?.stones),
        diamonds: normalizeDiamonds(it?.diamonds),
        itemCode: it?.itemCodeDetail?.value
          ? {
              value: it?.itemCodeDetail?.value,
              label: it?.itemCodeDetail?.label,
            }
          : "",
        gstPercent: gst,
      }));

    return payload;
  };
  // -------------------------------------------------------

  const onSubmit = async (data) => {
    if (!data?.branch?.value) return toast.error("Select branch first.");
    if (!data?.customer?.value) return toast.error("Pick a customer.");
    if (!data?.invoiceDate) return toast.error("Pick estimate date.");

    const walletLine = (data?.payments || []).find((p) => p?.mode === "Wallet");
    if (walletLine) {
      const wallet = num(customer?.info?.balanceAmount);
      const walletAmt = num(walletLine?.amount);
      if (walletAmt > wallet + 0.0001) {
        return toast.error(`Wallet balance (₹${wallet.toFixed(2)}).`);
      }
    }

    data.estimateTemplate = selRes?.estimateTemplateKey;
    data.gstPercent = gst;
    const payload = buildPayload(data);

    try {
      if (mode === "edit" && itemId) {
        const res = await updateEstimate({ id: itemId, ...payload }).unwrap();
        toast.success("Estimate updated.");
        const updated = res?.data ?? res;
        const id = updated?._id || itemId;

        if (id) setPrintPrompt({ open: true, invoiceId: id });
        setTimeout(() => navigate("/estimate/estimate-list"), 500);
      } else {
        // return
        const res = await addEstimate(payload).unwrap();
        toast.success("Estimate created.");
        const created = res?.data ?? res;
        const id = created?._id;

        if (id) setPrintPrompt({ open: true, invoiceId: id });
        reset(DEFAULTS);
      }

      dispatch(
        stockApi.util.invalidateTags([{ type: "LabelledStock", id: "LIST" }]),
      );
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.error ||
        err?.message ||
        "Failed to save estimate";
      toast.error(msg);
    }
  };

  const onSelectQuotation = (data) => {
    const info = data?.info;
    setValue("customer", {
      value: info?.customerId,
      label: info?.customerCode,
    });
    const stocks = data?.info?.stockEntries?.map((item) => {
      return {
        rateSource: "quotation",
        itemCodeDetail: item?.labelledItemId
          ? { value: item?.labelledItemId, label: item?.itemCode }
          : "",
        itemCode: item?.labelledItemId
          ? { value: item?.labelledItemId, label: item?.itemCode }
          : "",
        category: item?.categoryId
          ? { value: item?.categoryId, label: item?.categoryName }
          : "",
        product: item?.productId
          ? { value: item?.productId, label: item?.productName }
          : "",
        design: item?.designId
          ? { value: item?.designId, label: item?.designName }
          : "",
        purity: item?.purityId
          ? { value: item?.purityId, label: item?.purityName }
          : "",
        counterRow: item?.counterId
          ? { value: item?.counterId, label: item?.purityName }
          : "",
        totalWt: item?.totalWt ? item?.totalWt : "",
        packingWt: item?.packingWt ? item?.packingWt : "",
        grossWt: item?.grossWt ? item?.grossWt : "",
        stoneWt: item?.stoneWt ? item?.stoneWt : "",
        diamondWt: item?.diamondWt ? item?.diamondWt : "",
        netWt: item?.netWt ? item?.netWt : "",
        qty: item?.qty ? item?.qty : "",
        pieces: item?.pieces ? item?.pieces : "",
        stonePcs: item?.stonePcs ? item?.stonePcs : "",
        diamondPcs: item?.diamondPcs ? item?.diamondPcs : "",
        ratePerGram: item?.ratePerGram,
        makingPercentage: item?.makingPercentage ? item?.makingPercentage : "",
        makingPerGram: item?.makingPerGram ? item?.makingPerGram : "",
        fixedWastage: item?.fixedWastage ? item?.fixedWastage : "",
        fixedAmount: item?.fixedAmount ? item?.fixedAmount : "",
        stoneAmount: item?.stoneAmount ? item?.stoneAmount : "",
        diamondAmount: item?.diamondWt ? item?.diamondWt : "",
        hallmarkAmount: item?.hallmarkAmount ? item?.hallmarkAmount : "",
        itemAmount: item?.itemAmount ? item?.itemAmount : "",
        itemGstAmount: item?.itemGstAmount ? item?.itemGstAmount : "",
        // itemDiscountAmount: item?.itemDiscountAmount
        //   ? item?.itemDiscountAmount
        //   : "",
        // itemAmountAfterDiscount: item?.itemAmountAfterDiscount
        //   ? item?.itemAmountAfterDiscount
        //   : "",
        totalItemAmount: item?.totalItemAmount ? item?.totalItemAmount : "",
        mrp: item?.mrp ? item?.mrp : "",
        soldBy: item?.soldById
          ? { value: item?.soldById, label: item?.soldByLabel }
          : "",
        // commissionPercent:item?.commissionPercent ? item?.mrp:"",
        // commissionAmount:item?.mrp ? item?.mrp:"",

        huidCode: item?.huidCode ? item?.huidCode : "",
      };
    });

    setValue("stockEntries", stocks);
  };

  const openEstimatePreview = async (id) => {
    if (!id) return;

    try {
      setEstimatePreview({ open: true, html: "", loading: true });

      const htmlContent = await downloadEstimateReceiptPdf(id).unwrap();

      setEstimatePreview({
        open: true,
        html: htmlContent,
        loading: false,
      });
    } catch (err) {
      toast.error("Failed to load estimate preview.");
      setEstimatePreview({ open: false, html: "", loading: false });
    }
  };

  const closeEstimatePreview = () => {
    setEstimatePreview({ open: false, html: "", loading: false });

    setTimeout(() => {
      if (mode === "create") focusCustomerSelect();
    }, 80);
  };

  return (
    <>
      {/* Quick Add Customer */}
      <QuickAddCustomerModal
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        selectedBranch={branchSel} // ✅ branch info bhej rahe
        onAdded={async (created) => {
          const justAdded =
            (handleDropDowns({ key: "customer", customers: [created] }) ||
              [])[0] || null;
          if (justAdded) onCustomerPicked(justAdded);
          lastCreatedRef.current = created;

          const searchKey =
            created?.customerId ||
            created?.contact ||
            created?.customerName ||
            "";
          setTinyArgs({
            page: 1,
            limit: 3,
            sortOrder: "desc",
            sortBy: "createdAt",
            search: String(searchKey),
            branchId: branchSel?.value || undefined, // ✅ tiny fetch bhi branch-wise
          });

          try {
            await refetchCustomers();
          } catch {}

          setQuickOpen(false);
        }}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
        {/* Top card */}
        <div className="bg-white shadow-sm rounded-lg px-3 py-2 border">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
            {topFields?.map((item, idx) => {
              const isCustomer = item?.name === "customer";
              const isQuotation = item?.name === "quotationNumber";
              const isInvoiceNo = item?.name === "invoiceNo";
              const isBranch = item?.name === "branch";
              const cellSpan = isCustomer ? "sm:col-span-2 lg:col-span-2" : "";
              const hasErr = !!errors?.[item.name];

              return (
                <div key={idx} className={`min-w-0 ${cellSpan}`}>
                  <label className="block text-xs font-semibold text-slate-600 ">
                    {item?.label}{" "}
                    {item?.mandatory && <span className="text-red-500">*</span>}
                  </label>

                  {/* TEXT */}
                  {item?.type === "text" && (
                    <>
                      <input
                        {...register(item.name, {
                          required: item?.mandatory
                            ? `${item.label} is required`
                            : false,
                          onChange: (e) => {
                            if (item?.name === "invoiceNo") {
                              const has = `${e.target.value}`.trim();
                              setValue("gstRoundOff", !!has);
                              setValue("gstApply", !!has);
                            }
                          },
                        })}
                        disabled={
                          !item?.visibility ||
                          item?.disabled ||
                          item?.name === "invoiceNo"
                        }
                        placeholder={
                          isInvoiceNo && nextNumber
                            ? `Next: ${nextNumber}`
                            : `Enter ${item.label}`
                        }
                        className={inputCls(hasErr)}
                      />
                    </>
                  )}

                  {/* NUMBER */}
                  {item?.type === "number" && (
                    <input
                      type="number"
                      {...register(item.name, {
                        required: item?.mandatory
                          ? `${item.label} is required`
                          : false,
                      })}
                      placeholder={`Enter ${item.label}`}
                      className={inputCls(hasErr)}
                    />
                  )}

                  {/* DATE */}
                  {item?.type === "date" && (
                    <input
                      type="date"
                      max={today} // future date allowed nahi
                      {...register(item.name, {
                        required: item?.mandatory
                          ? `${item.label} is required`
                          : false,
                        validate: (value) =>
                          !value || value <= today || "Future date not allowed",
                      })}
                      className={inputCls(hasErr, "py-0.5", item?.type)}
                    />
                  )}

                  {/* DROPDOWN */}
                  {item?.type === "dropdown" && (
                    <Controller
                      name={item.name}
                      control={control}
                      rules={{
                        required: item?.mandatory
                          ? `${item.label} is required`
                          : false,
                      }}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex items-center gap-2">
                          <div className="w-full">
                            <Select
                              ref={isBranch ? customerSelectRef : null}
                              value={value}
                              options={handleDropDowns({
                                key: item["name"],
                                categories: categoriesFromState,
                                products: productsFromState,
                                designs: designFromState,
                                purities: purityFromState,
                                category: "",
                                product: "",
                                branches: branchesFromState,
                                customers,
                                quotations,
                              })}
                              styles={
                                isCustomer
                                  ? withErrorBorder(
                                      customerSelectStyles,
                                      hasErr,
                                    )
                                  : withErrorBorder(baseSelectStyles, hasErr)
                              }
                              classNamePrefix="react-select"
                              menuPlacement="bottom"
                              isClearable
                              isDisabled={
                                (isCustomer && !branchSel?.value) || // ✅ branch select nahi to customer disable
                                (isQuotation && !branchSel?.value) ||
                                (quotationNumber && isCustomer)
                              }
                              placeholder={
                                (isCustomer && !branchSel?.value) ||
                                (isQuotation && !branchSel?.value)
                                  ? "Select branch first"
                                  : `Select ${item.label}`
                              }
                              onChange={(opt) => {
                                onChange(opt ?? null);
                                if (isCustomer) onCustomerPicked(opt);
                                if (isQuotation) {
                                  onQuotationPicked(opt);
                                  onSelectQuotation(opt);
                                }
                              }}
                              onInputChange={(input, meta) => {
                                if (!isCustomer && !isQuotation) return;
                                if (isCustomer) {
                                  if (meta.action === "input-change")
                                    setCustomerSearch(input.trim());
                                }
                                if (isQuotation) {
                                  if (meta.action === "input-change")
                                    setQuotSearch(input.trim());
                                }
                              }}
                              isLoading={
                                (isCustomer && loadingCustomers) ||
                                (isQuotation && quotLoading)
                              }
                              filterOption={(candidate, raw) => {
                                if (!raw) return true;
                                const text = (candidate?.label || "")
                                  .toString()
                                  .toLowerCase();
                                return text.includes(raw.toLowerCase());
                              }}
                            />
                          </div>

                          {isCustomer && (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!branchSel?.value) {
                                    toast.error("Select branch first.");
                                    return;
                                  }
                                  setQuickOpen(true);
                                }}
                                className="px-4 py-1.5 rounded-md border text-xs text-[#6b59c6] hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-[#9481ce]"
                                title="Quick add customer"
                              >
                                +
                              </button>
                              <CustomerAvatarHoverCard
                                size="md"
                                tone="violet"
                                customer={customer}
                                info={{
                                  name: customer?.info?.customerName,
                                  code: customer?.info?.customerId,
                                  phone: customer?.info?.customerId,
                                  email: customer?.info?.email,
                                  address:
                                    `${
                                      customer?.permanantAddress
                                        ?.addressLine1 || ""
                                    } ` +
                                    `${customer?.info?.city || ""} ` +
                                    `${customer?.info?.state || ""} ` +
                                    `${customer?.info?.country || ""}`,
                                  deliveryAddress: (() => {
                                    const def =
                                      customer?.info?.deliveryAddresses?.find(
                                        (d) => d.isDefault,
                                      ) ||
                                      customer?.info?.deliveryAddresses?.[0];
                                    if (!def) return "";
                                    return `${def.addressLine1 || ""} ${
                                      def.city || ""
                                    } ${def.state || ""} ${def.country || ""} ${
                                      def.pincode || ""
                                    }`;
                                  })(),
                                  since: customer
                                    ? formatDate(customer?.info?.createdAt)
                                    : "",
                                  fineGold: customer?.info?.fineGold,
                                  fineSilver: customer?.info?.fineSilver,
                                  walletAmount: customer?.info?.balanceAmount,
                                  outstandingAmount:
                                    customer?.info?.outstandingAmount, // 🧾
                                }}
                              />
                            </>
                          )}
                        </div>
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Item type tabs + Bulk + Summary */}
        <div className="flex items-center justify-between pb-0 mb-0">
          <InvoiceItemTypeTabs
            value={itemType}
            onChange={(opt) => setItemType(opt)}
            className="mx-0"
          />

          {itemType === "ornaments" && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="relative  inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-white shadow-sm px-1 py-1"
              ref={summaryRef}
            >
              <input
                type="text"
                title="Rate for all"
                placeholder="Rate for all"
                value={bulkRate}
                onChange={(e) => setBulkRate(e.target.value)}
                onBlur={applyRateForAll}
                onKeyDown={(e) => e.key === "Enter" && applyRateForAll()}
                className="w-24 md:w-28 px-2 py-1 text-xs border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />

              <input
                type="text"
                title="Hallmark for all"
                placeholder="Hallmark for all"
                value={bulkHallmark}
                onChange={(e) => setBulkHallmark(e.target.value)}
                onBlur={applyHallmarkForAll}
                onKeyDown={(e) => e.key === "Enter" && applyHallmarkForAll()}
                className="w-24 md:w-32 px-2 py-1 text-xs border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />

              <button
                type="button"
                title="Overall summary"
                onClick={() => setSumOpen((s) => !s)}
                className="px-2 py-1.5 text-[11px] rounded-lg transition-colors text-slate-600 hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
                aria-expanded={sumOpen}
                aria-haspopup="dialog"
              >
                <FaCartShopping />
              </button>

              {sumOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-[calc(100%+10px)] right-0 min-w-[22rem] rounded-2xl border border-slate-200 bg-white shadow-2xl p-0 text-xs z-[1000]"
                  role="dialog"
                  aria-labelledby="orn-sum-title"
                >
                  <span
                    className="absolute -top-2 right-4 h-4 w-4 rotate-45 border border-slate-200 border-b-0 border-r-0 shadow-sm bg-[#9481ce]"
                    aria-hidden
                  />

                  <div className="relative px-3 py-2 rounded-t-2xl bg-[#9481ce] text-white border border-1 border-[#9481ce]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-white/20">
                          <FaCartShopping className="text-white text-[12px]" />
                        </span>
                        <span
                          id="orn-sum-title"
                          className="font-semibold text-[12.5px] tracking-wide"
                        >
                          Ornaments Summary
                        </span>
                      </div>
                      <span className="text-[10px] opacity-90">live</span>
                    </div>
                  </div>

                  <div className="p-3 text-slate-700 tabular-nums border border-1 border-[#9481ce]">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Items</span>
                        <span className="font-medium">{ornSummary.rows}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Qty</span>
                        <span className="font-medium">{ornSummary.qty}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Pcs</span>
                        <span className="font-medium">{ornSummary.pieces}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Stn Pcs</span>
                        <span className="font-medium">
                          {ornSummary.stonePcs}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Dia Pcs</span>
                        <span className="font-medium">
                          {ornSummary.diamondPcs}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Total Wt</span>
                        <span className="font-semibold">
                          {ornSummary.totalWt.toFixed(3)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Packing Wt</span>
                        <span className="font-medium">
                          {ornSummary.packingWt.toFixed(3)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Gross Wt</span>
                        <span className="font-semibold">
                          {ornSummary.grossWt.toFixed(3)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Stn Wt</span>
                        <span className="font-medium">
                          {ornSummary.stoneWt.toFixed(3)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Dia Wt (g)</span>
                        <span className="font-medium">
                          {ornSummary.diamondGram.toFixed(3)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Net Wt</span>
                        <span className="font-semibold">
                          {ornSummary.netWt.toFixed(3)}
                        </span>
                      </div>

                      <div className="col-span-2 my-1 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Stn Amt</span>
                        <span className="font-medium">
                          ₹ {ornSummary.stoneAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Dia Amt</span>
                        <span className="font-medium">
                          ₹ {ornSummary.diamondAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Hallmark</span>
                        <span className="font-medium">
                          ₹ {ornSummary.hallmarkAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Item Amt</span>
                        <span className="font-semibold">
                          ₹ {ornSummary.itemAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Tax</span>
                        <span className="font-medium">
                          ₹ {ornSummary.itemGstAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">MRP</span>
                        <span className="font-medium">
                          ₹ {ornSummary.mrp.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Final Amt</span>
                        <span className="font-semibold">
                          ₹ {ornSummary.finalAmt.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                      <span>Auto-updates with rows</span>
                      <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700">
                        preview
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </div>

        {/* Items area */}
        <div className="bg-white shadow-sm rounded-xl p-0 mb-2 z-10">
          {itemType === "ornaments" && (
            <div
              ref={ornamentsListRef}
              className={`relative ${CARD_HEIGHT_CLASS} overflow-y-auto scroll-smooth snap-y snap-mandatory`}
            >
              {ornamentFields?.length
                ? ornamentFields?.map((field, idx) => (
                    <EstimateItemForm
                      key={field.id}
                      control={control}
                      register={register}
                      unregister={unregister}
                      watch={watch}
                      setValue={setValue}
                      getValues={getValues}
                      errors={errors}
                      rowindex={idx}
                      fieldId={field.id}
                      mode={mode}
                      showDelete={mode !== "edit" && allowMultipleRows}
                      onDelete={
                        mode !== "edit" && allowMultipleRows
                          ? () => {
                              const targetIdx = Math.max(0, idx - 1);

                              // ❌ unregister(rowPath)  <-- REMOVE THIS
                              // ❌ clearErrors(rowPath) <-- optional remove too

                              ornamentRemove(idx);

                              // optional: clear all errors for array (safe)
                              clearErrors("stockEntries");

                              requestAnimationFrame(() =>
                                setTimeout(
                                  () => focusRowAt(ornamentsListRef, targetIdx),
                                  0,
                                ),
                              );
                            }
                          : undefined
                      }
                      cardHeightClass={CARD_HEIGHT_CLASS}
                      registerFirstField={registerFirstField}
                      selectedBranch={selectedBranch}
                      manualAdjustmentMode={manualAdjustmentMode}
                    />
                  ))
                : ""}

              {ornamentFields.length === 0 && (
                <motion.div
                  tabIndex={-1}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0 }}
                  className="m-2 p-4 border border-gray-200 hover:border-gray-300 shadow-lg rounded-lg bg-white text-sm text-gray-600"
                >
                  No rows. Click “Add Row”.
                </motion.div>
              )}
            </div>
          )}

          {itemType === "loose" && (
            <div
              ref={looseStoneListRef}
              className={`relative ${CARD_HEIGHT_CLASS} overflow-y-auto scroll-smooth snap-y snap-mandatory`}
            >
              {looseFields.map((field, idx) => (
                <LooseStoneItemForm
                  key={field.id}
                  control={control}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  getValues={getValues}
                  errors={errors}
                  rowindex={idx}
                  fieldId={field.id}
                  showDelete={mode !== "edit" && allowMultipleRows}
                  onDelete={
                    mode !== "edit" && allowMultipleRows
                      ? () => {
                          const targetIdx = Math.max(0, idx - 1);
                          const rowPath = `looseStones.${idx}`;
                          try {
                            unregister(rowPath);
                          } catch {}
                          clearErrors(rowPath);
                          looseRemove(idx);
                          requestAnimationFrame(() =>
                            setTimeout(
                              () => focusRowAt(looseStoneListRef, targetIdx),
                              0,
                            ),
                          );
                        }
                      : undefined
                  }
                  cardHeightClass={CARD_HEIGHT_CLASS}
                  registerFirstField={registerFirstField}
                  selectedBranch={selectedBranch}
                />
              ))}

              {looseFields.length === 0 && (
                <motion.div
                  tabIndex={-1}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0 }}
                  className="m-2 p-4 border border-gray-200 hover:border-gray-300 shadow-lg rounded-lg bg-white text-sm text-gray-600"
                >
                  No rows. Click “Add Row”.
                </motion.div>
              )}
            </div>
          )}

          {itemType === "looseDiamond" && (
            <div
              ref={looseDiamondListRef}
              className={`relative ${CARD_HEIGHT_CLASS} overflow-y-auto scroll-smooth snap-y snap-mandatory`}
            >
              {looseDiaFields.map((field, idx) => (
                <LooseDiamondItemForm
                  key={field.id}
                  control={control}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  getValues={getValues}
                  errors={errors}
                  rowindex={idx}
                  fieldId={field.id}
                  showDelete={mode !== "edit" && allowMultipleRows}
                  onDelete={
                    mode !== "edit" && allowMultipleRows
                      ? () => {
                          const targetIdx = Math.max(0, idx - 1);
                          const rowPath = `looseDiamonds.${idx}`;
                          try {
                            unregister(rowPath);
                          } catch {}
                          clearErrors(rowPath);
                          looseDiaRemove(idx);
                          requestAnimationFrame(() =>
                            setTimeout(
                              () => focusRowAt(looseDiamondListRef, targetIdx),
                              0,
                            ),
                          );
                        }
                      : undefined
                  }
                  cardHeightClass={CARD_HEIGHT_CLASS}
                  registerFirstField={registerFirstField}
                  selectedBranch={selectedBranch}
                />
              ))}

              {looseDiaFields.length === 0 && (
                <motion.div
                  tabIndex={-1}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0 }}
                  className="m-2 p-4 border border-gray-200 hover:border-gray-300 shadow-lg rounded-lg bg-white text-sm text-gray-600"
                >
                  No rows. Click “Add Row”.
                </motion.div>
              )}
            </div>
          )}
        </div>

        {/* Summary footer */}
        <div className="rounded-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-1">
            {/* RIGHT: Metrics */}
            <div className="space-y-2">
              <div className="border rounded-lg px-2 py-2 bg-white">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-xs">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-slate-500 font-bold leading-4">
                      TAXABLE AMOUNT (pre-tax)
                    </span>
                    <span className="font-medium text-blue-600 shrink-0">
                      ₹ {taxableAmount?.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex items-start justify-between gap-3">
                    <span className="text-slate-500 font-bold leading-4">
                      MRP ITEMS (incl. tax)
                    </span>
                    <span className="font-medium text-blue-600 shrink-0">
                      ₹{" "}
                      {(
                        agg.ornMrpFinal +
                        agg.stoneMrpFinal +
                        agg.diaMrpFinal
                      ).toFixed(2)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500 font-bold">DISCOUNT</span>
                    <input
                      type="text"
                      {...register("totalDiscount")}
                      className="w-36 md:w-32 max-w-[160px] w-full md:w-32 px-2 py-1 border rounded-md text-right font-medium"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="flex items-start justify-between gap-3">
                    <span className="text-slate-500 font-bold leading-4">
                      SALE (pre-tax)
                    </span>
                    <span className="font-medium text-blue-600 shrink-0">
                      ₹ {salePreTax.toFixed(2)}
                      {/* {(
                        ornAfterDiscount +
                        stoneAfterDiscount +
                        diaAfterDiscount
                      ).toFixed(2)} */}
                    </span>
                  </div>

                  <div className="flex items-center  justify-between gap-3">
                    <span className="text-slate-500 font-bold ">
                      GST ({" " + ORNAMENT_GST + "% "} )
                      {/* <span className="text-[8px] font-medium text-slate-600 break-words block md:inline">
                        </span> */}
                      {/* (Orn {ORNAMENT_GST}% / Stones{" "}
                        {gstLooseWeightedRate.toFixed(2)}% / Diamond{" "}
                        {gstDiaWeightedRate.toFixed(2)}%) */}
                    </span>
                    <span className="font-medium text-blue-600 shrink-0">
                      ₹ {(gstOrn + gstLoose + gstDia).toFixed(2)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500 font-bold">COURIER</span>
                    <input
                      type="text"
                      {...register("courierCharge")}
                      className="w-36 md:w-32 max-w-[160px] w-full md:w-32 px-2 py-1 border rounded-md text-right font-medium"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500 font-bold">
                      TOTAL AMOUNT
                    </span>
                    <input
                      type="text"
                      value={
                        grandTotalManual === undefined ||
                        grandTotalManual === ""
                          ? computedGrandTotal.toFixed(2)
                          : grandTotalManual
                      }
                      onChange={(e) =>
                        setValue("grandTotalManual", e.target.value)
                      }
                      onBlur={handleGrandManualBlur}
                      className="w-36 md:w-32 max-w-[160px] w-full md:w-32 px-2 py-1 border rounded-md text-right font-semibold"
                    />
                  </div>

                  <div className="flex items-center justify-between  gap-3">
                    <span className="text-slate-500 font-bold">
                      TDS ({tds}%)
                    </span>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        onChange={(e) => setValue("tdsApply", e.target.checked)}
                        checked={!!tdsApply}
                        title="Apply TDS on taxable pre-tax sale (added to total)"
                      />
                      <span className="font-medium text-blue-600 shrink-0">
                        ₹ {tdsAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start justify-between gap-3">
                    <span className="text-slate-500 font-bold leading-4">
                      TOTAL PAYABLE
                    </span>
                    <span className="font-semibold text-blue-600 shrink-0">
                      ₹ {totalPayable.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex  items-start justify-between gap-3">
                    <span className="text-slate-500 font-bold leading-4">
                      PAID AMOUNT
                    </span>
                    <span className="font-medium text-blue-600 shrink-0">
                      ₹ {paid.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex items-start  justify-between  gap-3">
                    <span className="text-slate-500 font-bold leading-4">
                      ADVANCE AMOUNT
                    </span>
                    <span className="font-medium text-blue-600  shrink-0">
                      ₹ {Number(watch("advanceAmount") || 0).toFixed(2)}
                    </span>
                  </div>

                  <div className="flex items-start justify-between gap-3">
                    <span className="text-slate-500 font-bold leading-4">
                      BALANCE AMOUNT
                    </span>
                    <span
                      className={`font-semibold  shrink-0  ${
                        balanceNet < 0 ? "text-emerald-600" : "text-blue-600"
                      }`}
                    >
                      ₹ {balanceNet.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* LEFT: actions */}
            <div className="space-y-2 bg-white rounded-lg px-2 py-2 border">
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-12 sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 ">
                    Mode
                  </label>
                  <select
                    value={payMode}
                    onChange={(e) => setPayMode(e.target.value)}
                    className="w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300"
                  >
                    <option>Cash</option>
                    <option>UPI</option>
                    <option>Bank</option>
                    {/* <option>Wallet</option> */}
                  </select>
                </div>

                <div className="col-span-6 sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600">
                    Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={payAmt}
                    onChange={(e) => setPayAmt(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addPayment()}
                    placeholder="0.00"
                    className="w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label className="block text-xs font-semibold text-slate-600 ">
                    Ref No
                  </label>
                  <input
                    type="text"
                    value={payRef}
                    onChange={(e) => setPayRef(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addPayment()}
                    placeholder="Txn/Chq/UPI ID"
                    className="w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                </div>

                <div className="col-span-12 sm:col-span-4">
                  <label className="block text-xs font-semibold text-slate-600">
                    Description
                  </label>
                  <input
                    type="text"
                    value={payDesc}
                    onChange={(e) => setPayDesc(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addPayment()}
                    placeholder="Narration / reason"
                    className="w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                </div>

                <div className="col-span-12 sm:col-span-1">
                  <button
                    type="button"
                    onClick={addPayment}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#9481ce] hover:bg-[#9481ce] text-white py-2 px-2 rounded-md text-sm font-medium shadow-sm"
                  >
                    <FaPlus className="text-xs" />
                  </button>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[105px] overflow-y-auto">
                  <table className="min-w-full text-xs min-h-full">
                    <thead className="bg-slate-50 text-slate-600 sticky top-0 z-10">
                      <tr>
                        <th className="text-left px-2 py-1 border-b">#</th>
                        <th className="text-left px-3 py-2 border-b">Mode</th>
                        <th className="text-right px-3 py-2 border-b">
                          Amount (₹)
                        </th>
                        <th className="text-left px-3 py-2 border-b">Ref No</th>
                        <th className="text-left px-3 py-2 border-b">
                          Description
                        </th>
                        <th className="text-right px-3 py-2 border-b">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {payFields.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-3 py-3 text-center text-slate-500"
                          >
                            No payments added.
                          </td>
                        </tr>
                      ) : (
                        payFields.map((p, i) => (
                          <tr
                            key={p.id}
                            className="odd:bg-white even:bg-slate-50"
                          >
                            <td className="px-3 py-2">{i + 1}</td>
                            <td className="px-3 py-2">{p.mode}</td>
                            <td className="px-3 py-2 text-right">
                              {Number(p.amount).toFixed(2)}
                            </td>
                            <td className="px-3 py-2">{p.refNo || "—"}</td>
                            <td className="px-3 py-2">
                              {p?.description || "—"}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => payRemove(i)}
                                className="inline-flex text-center items-center gap-1 text-red-600 hover:text-red-700"
                                title="Remove"
                              >
                                <FaTrash className="text-[12px]" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2 bg-white rounded-xl p-2 mt-1 border">
            <div className="flex flex-wrap gap-2 justify-end items-center mt-1">
              {mode !== "edit" && allowMultipleRows && (
                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98, y: 0 }}
                  type="button"
                  onClick={handleAddRow}
                  className="
          group relative inline-flex items-center gap-2 rounded-full
          border border-sky-200 bg-sky-50 px-3.5 py-1.5 text-[11px] font-medium text-sky-700
          shadow-sm hover:bg-sky-100 hover:border-sky-300 hover:shadow-md transition-all
          w-full sm:w-auto justify-center sm:justify-start
        "
                >
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-sky-500 text-white">
                    <FaPlus className="text-[10px]" />
                  </span>
                  <span className="tracking-wide">Add Row</span>

                  <span className="hidden sm:inline ml-1 h-4 w-px bg-sky-200 group-hover:bg-sky-300" />

                  <span className="hidden sm:inline-flex items-center rounded-full bg-white/80 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-sky-700">
                    Ctrl + +
                  </span>
                </motion.button>
              )}

              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98, y: 0 }}
                type="submit"
                disabled={addState.isLoading || updateState.isLoading}
                className="
        group relative inline-flex items-center gap-2 rounded-full
        bg-emerald-500 px-4 py-1.5 text-[11px] font-semibold text-white
        shadow-sm hover:bg-emerald-600 hover:shadow-md transition-all
        disabled:opacity-70 disabled:cursor-not-allowed
        w-full sm:w-auto justify-center sm:justify-start
      "
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <FaCheck className="text-[10px]" />
                </span>
                <span className="tracking-wide">
                  {mode === "edit" ? "Update" : "Save"}
                </span>

                <span className="hidden sm:inline ml-1 h-4 w-px bg-emerald-300/80 group-hover:bg-white/60" />

                <span className="hidden sm:inline-flex items-center rounded-full bg-emerald-400/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide">
                  Ctrl + S
                </span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98, y: 0 }}
                type="button"
                onClick={() =>
                  reset(
                    mode === "edit" && existingData ? existingData : DEFAULTS,
                  )
                }
                className="
        group relative inline-flex items-center gap-2 rounded-full
        border border-slate-300 bg-slate-50 px-3.5 py-1.5 text-[11px] font-semibold text-slate-700
        shadow-sm hover:bg-slate-100 hover:border-slate-400 hover:shadow-md transition-all
        w-full sm:w-auto justify-center sm:justify-start
      "
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white">
                  <FaUndo className="text-[10px]" />
                </span>
                <span className="tracking-wide">Reset</span>

                <span className="hidden sm:inline ml-1 h-4 w-px bg-slate-200 group-hover:bg-slate-300" />

                <span className="hidden sm:inline-flex items-center rounded-full bg-white/80 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-slate-700">
                  Ctrl + R
                </span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98, y: 0 }}
                type="button"
                onClick={() => navigate("/estimate/estimate-list")}
                className="
        group relative inline-flex items-center gap-2 rounded-full
        bg-indigo-500 px-3.5 py-1.5 text-[11px] font-semibold text-white
        shadow-sm hover:bg-indigo-600 hover:shadow-md transition-all
        w-full sm:w-auto justify-center sm:justify-start
      "
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <FaList className="text-[10px]" />
                </span>
                <span className="tracking-wide">Estimate List</span>

                <span className="hidden sm:inline ml-1 h-4 w-px bg-indigo-300/80 group-hover:bg-white/60" />

                <span className="hidden sm:inline-flex items-center rounded-full bg-indigo-400/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide">
                  Ctrl + ⌫
                </span>
              </motion.button>
            </div>
          </div>
        </div>
      </form>

      {/* ✅ Print confirmation dialog */}
      <ConfirmDialog
        open={printPrompt.open}
        onCancel={() => {
          setPrintPrompt({ open: false, invoiceId: null });
          setTimeout(() => {
            if (mode === "create") {
              focusCustomerSelect(); // ✅ print cancel ke baad customer select pe focus
            }
          }, 0);
        }}
        onConfirm={() => {
          setTimeout(() => {
            if (mode === "create") {
              focusCustomerSelect(); // ✅ print ke baad bhi focus customer pe
            }
          }, 0);
          if (printPrompt.invoiceId) {
            const id = printPrompt.invoiceId;
            setPrintPrompt({ open: false, invoiceId: null });
            openEstimatePreview(id);
          }
          setPrintPrompt({ open: false, invoiceId: null });
        }}
        title="Estimate saved successfully"
        message="Would you like to open the estimate preview for printing?"
        confirmText="Yes, open & print"
        cancelText="No"
        loading={false}
        destructive={false}
      />

      <InvoicePrintPreviewModal
        open={estimatePreview.open}
        html={estimatePreview.html}
        loading={estimatePreview.loading}
        title="Estimate Preview"
        onClose={closeEstimatePreview}
      />
    </>
  );
};

export default React.memo(EstimateForm);
